import { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Shared filter state for the Carrier Engagement dashboard.
 *
 * The filter pills live in the TopBar, while the data table that consumes them
 * lives in the routed page — different branches of the tree. This provider
 * wraps the shell so both sides share one source of truth, mirroring
 * WorkflowBarProvider.
 *
 * Scope note: the model type, HCC, Qs-to-SmartLight, and workflow-step
 * (chevron) filters are wired to the backend today. The New Comments button
 * remains self-contained until its own ticket wires it up — it carries
 * selection state here only so the mutual-exclusivity rule with
 * Qs-to-SmartLight can live in one place.
 */
// Selection state for the filter pills (model type, HCC, …). Changes whenever
// any pill is toggled.
const CarrierEngagementFilterContext = createContext(null);
// The derived backend query, split into its own context so the data table can
// subscribe to it alone — toggling a pill that doesn't feed the query (e.g. HCC
// today) won't churn the table. See useCarrierEngagementApiFilters below.
const CarrierEngagementApiFiltersContext = createContext(null);

// Sentinel "show everything" value — selecting it clears the model type
// contribution to the backend query.
const ALL = 'All';
const DEFAULT_MODEL_TYPE = ALL;
const DEFAULT_HCC = 'Exclude HCC';
// Workflow-step chevrons: 'all' selects everything (no engagement_step param).
// The other keys are sent to the backend verbatim as engagement_step.
const ENGAGEMENT_STEP_ALL = 'all';
const DEFAULT_ENGAGEMENT_STEP = ENGAGEMENT_STEP_ALL;

function CarrierEngagementFilterProvider({ children }) {
  const [modelType, setModelType] = useState(DEFAULT_MODEL_TYPE);
  const [hcc, setHcc] = useState(DEFAULT_HCC);
  // Workflow-step chevron selection (All/Pending/Contesting/Agreed/Closed),
  // radio-style: exactly one active. Default 'all'.
  const [engagementStep, setEngagementStep] = useState(DEFAULT_ENGAGEMENT_STEP);
  // Single-select column-header filters (Client, TPA/Carrier, Referral Type,
  // Referral Scheme, Aging Out, C Score, Base Status, TPA/Carrier Status &
  // Reason, SL Response/Reason/Action). Keyed by the backend filter param so
  // the value flows to /api/v1/referrals/ verbatim. Empty string clears.
  const [columnFilters, setColumnFilters] = useState({});

  // Set or clear one column filter. Clearing ('' / null / undefined) drops the
  // key entirely so apiFilters never carries an empty param. Returns a fresh
  // object so apiFilters (and the table's refetch) react to the change.
  const setColumnFilter = useCallback((key, value) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (value === '' || value === null || value === undefined) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  }, []);

  // The two action buttons default to off (inactive/non-blue). Only one may be
  // active at a time — the toggles below enforce that mutual exclusivity.
  const [questionsToSmartlight, setQuestionsToSmartlight] = useState(false);
  const [newComments, setNewComments] = useState(false);

  // Toggle Qs-to-SmartLight; turning it on clears New Comments (mutually
  // exclusive). Stable identity so the header button isn't a fresh prop each
  // render.
  const toggleQuestionsToSmartlight = useCallback(() => {
    setQuestionsToSmartlight((on) => {
      if (!on) setNewComments(false);
      return !on;
    });
  }, []);

  // Toggle New Comments; turning it on clears Qs-to-SmartLight. New Comments is
  // not yet wired to the backend query (its own ticket) — this state exists so
  // the exclusivity rule has a single home.
  const toggleNewComments = useCallback(() => {
    setNewComments((on) => {
      if (!on) setQuestionsToSmartlight(false);
      return !on;
    });
  }, []);

  // The filter object sent to /api/v1/referrals/. Memoized so OverviewTable's
  // refetch effect only fires when a selection actually changes (not on every
  // render). This is the single home for the cross-group business rules.
  const apiFilters = useMemo(() => {
    const filters = { ...columnFilters };
    if (modelType && modelType !== ALL) {
      filters.model_type = modelType;
    }
    // HCC sends "Exclude HCC" / "HCC Only" verbatim; "All" is the absence of
    // the filter, so it's omitted. The backend validates the value.
    if (hcc && hcc !== ALL) {
      filters.hcc = hcc;
    }
    // Qs-to-SmartLight is a presence flag: send it only when active. New
    // Comments deliberately contributes nothing yet (unwired).
    if (questionsToSmartlight) {
      filters.questions_to_smartlight = 'true';
    }
    // Workflow-step bucket: 'all' is the absence of the filter, so it's
    // omitted. The other keys flow straight through to engagement_step.
    if (engagementStep && engagementStep !== ENGAGEMENT_STEP_ALL) {
      filters.engagement_step = engagementStep;
    }
    return filters;
  }, [modelType, hcc, columnFilters, questionsToSmartlight, engagementStep]);

  // Pill + column selection state lives in its own value, deliberately
  // excluding apiFilters. The table subscribes to apiFilters alone and the
  // pills/column dropdowns to this selection, so each side re-renders only on
  // what it reacts to. Bundling them would hand the table a new context
  // reference on every toggle — including ones that don't change the derived
  // query (e.g. re-selecting the active pill) — re-rendering it for nothing.
  const selection = useMemo(
    () => ({
      modelType,
      setModelType,
      hcc,
      setHcc,
      engagementStep,
      setEngagementStep,
      columnFilters,
      setColumnFilter,
      questionsToSmartlight,
      toggleQuestionsToSmartlight,
      newComments,
      toggleNewComments,
    }),
    [
      modelType,
      hcc,
      engagementStep,
      columnFilters,
      setColumnFilter,
      questionsToSmartlight,
      toggleQuestionsToSmartlight,
      newComments,
      toggleNewComments,
    ]
  );

  return (
    <CarrierEngagementFilterContext.Provider value={selection}>
      <CarrierEngagementApiFiltersContext.Provider value={apiFilters}>
        {children}
      </CarrierEngagementApiFiltersContext.Provider>
    </CarrierEngagementFilterContext.Provider>
  );
}

function useCarrierEngagementFilters() {
  const ctx = useContext(CarrierEngagementFilterContext);
  if (!ctx) {
    throw new Error(
      'useCarrierEngagementFilters must be used within a CarrierEngagementFilterProvider'
    );
  }
  return ctx;
}

function useCarrierEngagementApiFilters() {
  const ctx = useContext(CarrierEngagementApiFiltersContext);
  if (ctx === null) {
    throw new Error(
      'useCarrierEngagementApiFilters must be used within a CarrierEngagementFilterProvider'
    );
  }
  return ctx;
}

export {
  CarrierEngagementFilterProvider,
  useCarrierEngagementFilters,
  useCarrierEngagementApiFilters,
  DEFAULT_MODEL_TYPE,
  DEFAULT_HCC,
  DEFAULT_ENGAGEMENT_STEP,
  ENGAGEMENT_STEP_ALL,
};
